import React from "react";
import { Marker, Popup } from "react-leaflet";
import { Icon } from "leaflet";
import { useNavigate } from "react-router";
import {
  convertWind,
  relativeDateName,
  locationLabelFromNameAndSource,
} from "../util/util";
import L from "leaflet";

interface WindArrowMarkerProps {
  n: number;
  e: number;
  location_id: number;
  windsnelheid: number;
  windrichting: number;
  windstoot: number;
  windUnits: string;
  location_name: string;
  location_source: string; // now required
  timestampSecs?: number;
  isMeasurement: boolean;
}

// SVG arrow: points down by default, rotate for windrichting
function getWindArrowIcon(windrichting: number, isMeasurement: boolean): Icon {
  let arrowLine: string;
  let arrowHead: string;
  if (isMeasurement) {
    // Solid blue for measurements, bigger arrow head
    arrowLine = `<line x1='20' y1='10' x2='20' y2='54' stroke='blue' stroke-width='4' />`;
    arrowHead = `<polygon points="8,54 32,54 20,66" fill="blue" />`;
  } else {
    // Dashed blue/light blue for forecasts, bigger and more solid arrow head
    arrowLine = `<defs>
      <linearGradient id='forecastGrad' x1='0' y1='10' x2='0' y2='54' gradientUnits='userSpaceOnUse'>
        <stop offset='0%' stop-color='#2196f3' />
        <stop offset='100%' stop-color='#90caf9' />
      </linearGradient>
    </defs>
    <line x1='20' y1='10' x2='20' y2='54' stroke='url(#forecastGrad)' stroke-width='4' stroke-dasharray='6,6' />`;
    // Bigger, more solid arrow head: a polygon with a semi-transparent gradient fill
    arrowHead = `<polygon points="8,54 32,54 20,66" fill="url(#forecastGrad)" fill-opacity="0.85" stroke="url(#forecastGrad)" stroke-width="2" />`;
  }
  const svg = `
    <svg width="28" height="40" viewBox="0 0 40 70" xmlns="http://www.w3.org/2000/svg">
      <g transform="rotate(${windrichting},20,40)">
        ${arrowLine}
        ${arrowHead}
      </g>
    </svg>
  `;
  return new Icon({
    iconUrl: "data:image/svg+xml;base64," + btoa(svg),
    iconSize: [28, 40],
    iconAnchor: [14, 36],
    popupAnchor: [0, -36],
    className: "wind-arrow-marker",
  });
}

export const WindArrowMarker: React.FC<WindArrowMarkerProps> = ({
  n,
  e,
  location_id,
  windsnelheid,
  windrichting,
  windstoot,
  windUnits,
  location_name,
  location_source,
  timestampSecs,
  isMeasurement,
}) => {
  const navigate = useNavigate();

  if (!location_source) {
    console.error(
      `WindArrowMarker: location_source is empty for location_name='${location_name}', location_id=${location_id}`,
    );
    return null;
  }

  // Compose the label for wind/gust/unit
  const windLabel = `${
    windsnelheid !== undefined && windsnelheid !== null
      ? convertWind(windsnelheid, windUnits)
      : "-"
  } / ${
    windstoot !== undefined && windstoot !== null
      ? convertWind(windstoot, windUnits)
      : "-"
  } ${windUnits}`;

  // Compose the location label using util logic
  const locLabel = locationLabelFromNameAndSource(
    location_name,
    location_source ?? "",
  );

  // Compose a DivIcon with the label above the arrow
  const iconUrl = getWindArrowIcon(windrichting, isMeasurement).options
    .iconUrl as string;
  const iconHtml = `
    <div style="display:flex;flex-direction:column;align-items:center;">
      <div style="font-size:12px;font-weight:bold;color:#000;background:rgba(255,255,255,0.85);border-radius:4px;padding:0 4px;margin-bottom:0;white-space:nowrap;">${windLabel}</div>
      <div><img alt='${windrichting}' src='${iconUrl}' style='width:24px;height:36px;' /></div>
    </div>
  `;
  const windIcon = new L.DivIcon({
    className: "wind-arrow-marker-label",
    html: iconHtml,
    iconSize: [36, 48],
    iconAnchor: [18, 42],
    popupAnchor: [0, -42],
  });

  // Compute day suffix for location info link
  const nowTimestampSecs = Math.round(Date.now() / 1000);
  const daySuffix = `&day=${relativeDateName(
    timestampSecs ?? nowTimestampSecs,
  )}`;

  return (
    <Marker position={[n, e]} icon={windIcon}>
      <Popup
        eventHandlers={{
          click: () => navigate(`/locationinfo?location=${location_id}`),
        }}
      >
        <div style={{ textAlign: "center", minWidth: 120 }}>
          <div style={{ fontWeight: "bold", marginBottom: 4 }}>{locLabel}</div>
          <div>
            Windsnelheid:{" "}
            <b>
              {windsnelheid !== undefined && windsnelheid !== null
                ? convertWind(windsnelheid, windUnits)
                : "-"}{" "}
              {windUnits}
            </b>
          </div>
          <div>
            Windstoot:{" "}
            <b>
              {windstoot !== undefined && windstoot !== null
                ? convertWind(windstoot, windUnits)
                : "-"}{" "}
              {windUnits}
            </b>
          </div>
          <div>
            Richting:{" "}
            <b>
              {windrichting !== undefined && windrichting !== null
                ? Math.round(windrichting)
                : "-"}
              °
            </b>
          </div>
          <div style={{ marginTop: 8 }}>
            <span
              style={{
                color: "#1976d2",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() =>
                navigate(`/locationinfo?location=${location_id}${daySuffix}`)
              }
            >
              Bekijk locatie info
            </span>
          </div>
        </div>
      </Popup>
    </Marker>
  );
};
